import React, { useState } from 'react';
import { toast } from 'sonner';
import { Bot, Power } from 'lucide-react';
import { Panel, Disclaimer, StatusBadge } from '../../components/Primitives';
import { useFetch } from '../../lib/hooks';
import { TodayTrading, TradingResults } from '../../components/TradingResults';
import { api, errMsg, fmtUsd } from '../../lib/api';

function Metric({ label, value, tone = 'text-foreground' }) {
  return <div className="vj-card p-4"><div className="text-xs text-vj-grey">{label}</div><div className={`font-mono text-lg mt-1 ${tone}`}>{value}</div></div>;
}

export default function SIBot() {
  const { data: bot, reload } = useFetch('/bot/status', [], 15000);
  const [busy, setBusy] = useState(false);
  const a = bot?.analytics || {};

  const toggle = async () => {
    setBusy(true);
    try { const { data } = await api.post('/bot/toggle'); toast.success(`S.I. Bot ${data.active ? 'activated' : 'paused'}`); reload(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="sibot-page">
      <Panel testId="sibot-visualization">
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          <div className="relative grid place-items-center h-72">
            <div className="absolute w-60 h-60 rounded-full bg-vj-neon/10 blur-3xl" />
            <div className={`relative w-48 h-48 rounded-full border border-vj-neon/40 grid place-items-center ${bot?.active ? 'vj-pulse' : ''}`}>
              <div className="w-32 h-32 rounded-full border border-vj-gold/40 grid place-items-center bg-vj-charcoal/70 backdrop-blur">
                <Bot size={44} className="text-vj-neon vj-text-glow" />
              </div>
            </div>
            {bot?.active && <div className="absolute inset-0 vj-spin-slow pointer-events-none">
              {[0, 72, 144, 216, 288].map((deg) => (
                <div key={deg} className="absolute w-2 h-2 rounded-full bg-vj-gold" style={{ top: '50%', left: '50%', transform: `rotate(${deg}deg) translateX(110px)` }} />
              ))}
            </div>}
          </div>
          <div>
            <div className="flex items-center gap-3 mb-3">
              <StatusBadge status={bot?.active ? 'healthy' : 'paused'} />
              <span className="font-display text-2xl">{bot?.status || 'Offline'}</span>
            </div>
            <p className="text-sm text-vj-grey mb-5 max-w-md">{bot?.disclaimer || 'Super Intelligence Trading System. Market monitoring and paper trading.'}</p>
            <button onClick={toggle} disabled={busy} data-testid="sibot-toggle-button"
              className={`h-11 px-6 rounded-lg font-semibold text-sm inline-flex items-center gap-2 transition-all ${bot?.active ? 'border border-vj-red/40 text-vj-red hover:bg-vj-red/10' : 'bg-vj-neon text-vj-obsidian vj-glow-green hover:-translate-y-0.5'}`}>
              <Power size={16} /> {bot?.active ? 'Pause S.I. Bot' : 'Activate S.I. Bot'}
            </button>
          </div>
        </div>
      </Panel>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Metric label="Market Trend" value={a.trend || '—'} tone="text-vj-gold" />
        <Metric label="Momentum" value={a.momentum ?? '—'} tone={Number(a.momentum) >= 0 ? 'text-vj-neon' : 'text-vj-red'} />
        <Metric label="Volatility" value={`${a.volatility ?? '—'}%`} />
        <Metric label="Volume Index" value={a.volume_index ?? '—'} />
        <Metric label="Market Regime" value={a.regime || '—'} tone="text-vj-gold" />
        <Metric label="Risk Utilization" value={`${a.risk_utilization ?? '—'}%`} />
        <Metric label="Executed Trades" value={bot?.executed_trades ?? 0} />
        <Metric label="Win / Loss" value={`${bot?.wins ?? 0} / ${bot?.losses ?? 0}`} />
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Metric label="Realized P&L" value={fmtUsd(bot?.realized_pnl)} tone={Number(bot?.realized_pnl) >= 0 ? 'text-vj-neon' : 'text-vj-red'} />
        <Metric label="Unrealized P&L" value={fmtUsd(bot?.unrealized_pnl)} />
        <Metric label="Fees" value={fmtUsd(bot?.fees)} />
        <Metric label="Allocation" value={fmtUsd(bot?.allocation)} tone="text-vj-gold" />
      </div>

      <TodayTrading bot={bot} />
      <TradingResults />

      <Disclaimer>Analytics are derived from live market monitoring and paper-trading records. The S.I. Bot does not claim predictive certainty or guaranteed performance. No real exchange orders are placed in this environment.</Disclaimer>
    </div>
  );
}
